import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { MuiTelInput as PhoneField } from 'mui-tel-input';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

import EditLocationSchedule from '@/components/location/schedule/EditLocationSchedule';
import { isValidPhone } from '@/forms';
import { AddressSearch, MerchantSelect, type Place } from '@/forms/components';
import { useCreateLocationMutation } from '@/store/api/services/locations/location';
import { useGetPlatformsQuery } from '@/store/api/services/platforms/platforms';

import type { Merchant } from '@/types';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

const CreateLocationSchema = z
  .object({
    place_id: z.string().optional(),
    hours: z.array(z.string()),
    name: z.string().min(2, 'Location Name is Required'),
    business_contact_name: z.string().optional(),
    business_contact_phone: isValidPhone(z.string().optional(), 'Invalid Phone Number'),
    merchant_id: z.string().optional(),
    street_address: z.string().min(2, 'Valid Address is Required'),
    address2: z.string().optional(),
    city: z.string().min(2, 'Valid City is Required'),
    zipcode: z.string().min(5, 'Valid Zipcode is Required'),
    state: z.string().min(2, 'Valid State is Required'),
    country: z.string().min(2, 'Valid Country is Required'),
    vault_branch: z.string().optional(),
    service_branch: z.string().optional(),
    profile_completed: z.boolean().optional(),
    type: z.enum(['native', 'teller']),
    platform_key: z.string().trim().optional(),
    platform_location_id: z.string().trim().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type === 'teller') {
      if (!data.platform_key) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Platform is required for teller locations',
          path: ['platform_key'],
        });
      }
      if (!data.platform_location_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Platform Location ID is required for teller locations',
          path: ['platform_location_id'],
        });
      }
    }
  });

type CreateLocationSchemaType = z.infer<typeof CreateLocationSchema>;

const CreateLocation = ({ hide }: ModalChildrenProps) => {
  const [lastSearch, setLastSearch] = useState<Place>();
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateLocationSchemaType>({
    resolver: zodResolver(CreateLocationSchema),
    mode: 'onSubmit',
    defaultValues: {
      type: 'native',
      platform_key: '',
      platform_location_id: '',
    },
  });

  const selectedType = watch('type');

  useEffect(() => {
    setValue('hours', ['24/7']);
  }, [setValue]);

  const [createLocation] = useCreateLocationMutation();
  const { data: platformsData } = useGetPlatformsQuery();

  const merchantPlatform = selectedMerchant?.platforms?.[0];
  const merchantPlatformKey = merchantPlatform
    ? platformsData?.data?.find((p) => p.id === merchantPlatform.platform_id)?.key
    : undefined;
  const isPlatformInheritedFromMerchant = !!merchantPlatformKey;

  useEffect(() => {
    if (selectedMerchant?.type === 'teller') {
      setValue('type', 'teller');
    }
    if (merchantPlatformKey) {
      setValue('platform_key', merchantPlatformKey);
    }
  }, [selectedMerchant, merchantPlatformKey, setValue]);

  const onSubmit = async (payload: CreateLocationSchemaType) => {
    if (
      lastSearch?.street_address !== payload.street_address ||
      lastSearch?.address2 !== payload.address2 ||
      lastSearch?.city !== payload.city ||
      lastSearch?.country !== payload.country ||
      lastSearch?.state !== payload.state ||
      lastSearch?.zipcode !== payload.zipcode
    ) {
      payload.place_id = undefined;
    }
    const {
      vault_branch,
      service_branch,
      profile_completed,
      type,
      platform_key,
      platform_location_id,
      ...rest
    } = payload;
    const createPayload = {
      ...rest,
      type,
      ...(type === 'teller' && platform_key && { platform_key }),
      ...(type === 'teller' && platform_location_id && { platform_location_id }),
      ...(vault_branch !== undefined && vault_branch !== '' && { vault_branch }),
      ...(service_branch !== undefined && service_branch !== '' && { service_branch }),
      ...(profile_completed !== undefined && { profile_completed }),
    };
    try {
      await createLocation(createPayload).unwrap();
      toast.success('Location created successfully');
      hide();
    } catch (error: any) {
      if (error?.message) {
        const err: { message: string } = error;
        toast.error(err.message);
      }
    }
  };

  const handleAddressSearch = (place: Place) => {
    Object.keys(place).forEach((key) => {
      setValue(key as keyof CreateLocationSchemaType, place[key], { shouldValidate: true });
    });
    setLastSearch(place);
  };

  return (
    <>
      <DialogContent sx={{ py: 4 }}>
        <Stack gap={2}>
          <TextField
            label="Location Name *"
            fullWidth
            error={!!errors.name}
            helperText={errors?.name?.message}
            size="small"
            {...register('name')}
          />
          <TextField
            label="Business Contact Name"
            size="small"
            fullWidth
            error={!!errors.business_contact_name}
            helperText={errors?.business_contact_name?.message}
            {...register('business_contact_name')}
          />
          <Controller
            name="business_contact_phone"
            control={control}
            defaultValue=""
            render={({ field: { value, onChange } }) => (
              <PhoneField
                defaultCountry="US"
                label="Business Contact Phone"
                size="small"
                value={value}
                onChange={(_, info) => onChange(info.numberValue ?? '')}
                error={!!errors.business_contact_phone}
                helperText={errors?.business_contact_phone?.message}
              />
            )}
          />

          <Controller
            name="merchant_id"
            control={control}
            defaultValue=""
            render={({ field: { onChange } }) => (
              <MerchantSelect
                label="Merchant"
                labelProps={{ sx: { fontSize: 14 }, error: !!errors.merchant_id }}
                buttonProps={{ size: 'medium' }}
                textFieldProps={{
                  size: 'small',
                  error: !!errors.merchant_id,
                  helperText: errors.merchant_id?.message,
                }}
                value={selectedMerchant}
                onChange={(val) => {
                  setSelectedMerchant(val);
                  onChange(val?.id ?? '');
                }}
              />
            )}
          />

          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.type}>
                <InputLabel>Type</InputLabel>
                <Select {...field} label="Type" value={field.value || ''}>
                  <MenuItem value="native">Native</MenuItem>
                  <MenuItem value="teller">Teller</MenuItem>
                </Select>
                {errors.type && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.type.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          {selectedType === 'teller' && (
            <>
              <Controller
                name="platform_key"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.platform_key}
                    disabled={isPlatformInheritedFromMerchant}
                  >
                    <InputLabel>Platform</InputLabel>
                    <Select
                      {...field}
                      label="Platform"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {platformsData?.data?.map((platform) => (
                        <MenuItem key={platform.id} value={platform.key}>
                          {platform.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {isPlatformInheritedFromMerchant && (
                      <Typography variant="caption" sx={{ mt: 0.5, ml: 1.75 }}>
                        Inherited from selected merchant
                      </Typography>
                    )}
                    {errors.platform_key && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {errors.platform_key.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
              <TextField
                label="Platform Location ID"
                size="small"
                fullWidth
                error={!!errors.platform_location_id}
                helperText={errors?.platform_location_id?.message}
                {...register('platform_location_id')}
              />
            </>
          )}

          <Divider sx={{ my: 2 }} />

          <AddressSearch onPlaceSelect={handleAddressSearch} size="small" />

          <TextField
            label="Address *"
            size="small"
            fullWidth
            error={!!errors.street_address}
            helperText={errors?.street_address?.message}
            {...register('street_address')}
          />

          <TextField
            label="Apartment, suite, etc. *"
            size="small"
            fullWidth
            error={!!errors.address2}
            helperText={errors?.address2?.message}
            {...register('address2')}
          />

          <Stack gap={2} direction="row">
            <TextField
              label="City *"
              size="small"
              fullWidth
              error={!!errors.city}
              helperText={errors?.city?.message}
              {...register('city')}
            />
            <TextField
              label="Zipcode *"
              size="small"
              fullWidth
              error={!!errors.zipcode}
              helperText={errors?.zipcode?.message}
              {...register('zipcode')}
            />
          </Stack>

          <Stack gap={2} direction="row">
            <TextField
              label="State *"
              size="small"
              fullWidth
              error={!!errors.state}
              helperText={errors?.state?.message}
              {...register('state')}
            />
            <TextField
              label="Country *"
              size="small"
              fullWidth
              error={!!errors.country}
              helperText={errors?.country?.message}
              {...register('country')}
            />
          </Stack>

          <Divider sx={{ mt: 2 }} />

          <TextField
            label="Vault branch"
            size="small"
            fullWidth
            placeholder="Branch responsible for vaulting"
            {...register('vault_branch')}
          />
          <TextField
            label="Service branch"
            size="small"
            fullWidth
            placeholder="Branch responsible for servicing"
            {...register('service_branch')}
          />
          <Controller
            name="profile_completed"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!!value}
                    onChange={(_, checked) => onChange(checked)}
                  />
                }
                label="Profiles completed"
              />
            )}
          />

          <Divider sx={{ mt: 2 }} />

          <Controller
            name="hours"
            control={control}
            render={({ field: { value, onChange } }) => (
              <EditLocationSchedule initValue={value} onChange={onChange} />
            )}
          />
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={hide}>Discard</Button>
        <LoadingButton variant="contained" loading={isSubmitting} onClick={handleSubmit(onSubmit)}>
          Create Location
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default CreateLocation;
